import {
  JobContactEntry,
  IncludeOnInvoiceMetadata,
  CallMetadata,
  StatusUpdateMetadata,
  VendorMessageMetadata,
  ClientMessageMetadata,
  FileUploadMetadata,
  EmailMetadata,
  SystemMessageMetadata
} from '../../types';
import {
  MessageSquare,
  Phone,
  Mail,
  Paperclip,
  DollarSign,
  Activity,
  Truck,
  User,
  Bot,
  LucideIcon
} from 'lucide-react';

export const parseMetadata = (entry: JobContactEntry): any => {
  if (!entry.metadata) return null;
  
  try {
    return JSON.parse(entry.metadata);
  } catch (error) {
    console.error('Error parsing metadata for entry', entry.id, ':', error);
    console.error('Metadata string:', entry.metadata);
    return null;
  }
};

export const getEntryTypeDisplayInfo = (entry: JobContactEntry) => {
  // Safety check for entry
  if (!entry || !entry.entryTypeId) {
    return {
      title: 'Unknown Entry',
      subtitle: '',
      badge: 'Unknown',
      badgeColor: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  }

  const metadata = parseMetadata(entry);
  
  switch (entry.entryTypeId) {
        case 5: // Include on Invoice
          if (metadata) {
            // Support both camelCase (new) and PascalCase (legacy) keys
            const m: any = metadata as any;
            const quantity = m.quantity ?? m.Quantity ?? 0;
            const rate = m.rate ?? m.Rate ?? 0;
            const amount = m.amount ?? m.Amount ?? (quantity * rate);
            const description = m.description ?? m.Description ?? 'Billable Item';
            const unit = m.unit ?? m.Unit ?? 'each';
            const isTaxable = m.isTaxable ?? m.IsTaxable ?? false;

            return {
              title: description,
              subtitle: `${quantity || 0} ${unit} @ $${Number(rate || 0).toFixed(2)}`,
              amount: Number(amount || 0),
              isTaxable: Boolean(isTaxable),
              badge: 'Billable',
              badgeColor: 'bg-green-100 text-green-800 border-green-200'
            };
          }
      break;

    case 2: // Call
      if (metadata) {
        const callData = metadata as CallMetadata;
        
        // Handle both PascalCase (old data) and camelCase (new data) formats
        const callDirection = callData.callDirection || (metadata as any).CallDirection;
        const callType = callData.callType || (metadata as any).CallType;
        const phoneNumber = callData.phoneNumber || (metadata as any).PhoneNumber;
        const contactPerson = callData.contactPerson || (metadata as any).ContactPerson;
        const durationMinutes = callData.durationMinutes || (metadata as any).DurationMinutes;
        
        return {
          title: `${callDirection || 'Unknown'} Call`,
          subtitle: contactPerson 
            ? `to ${contactPerson}` 
            : phoneNumber 
            ? `to ${phoneNumber}` 
            : 'Call logged',
          duration: durationMinutes,
          callType: callType,
          badge: callDirection || 'Call',
          badgeColor: callDirection === 'Inbound' 
            ? 'bg-blue-100 text-blue-800 border-blue-200'
            : 'bg-purple-100 text-purple-800 border-purple-200'
        };
      }
      break;

    case 6: // Status Update
      if (metadata) {
        // Support both camelCase and PascalCase
        const m: any = metadata as any;
        const previousStatus = m.previousStatus ?? m.PreviousStatus ?? '';
        const newStatus = m.newStatus ?? m.NewStatus ?? '';
        const reason = m.reason ?? m.Reason ?? '';
        const updatedBy = m.updatedBy ?? m.UpdatedBy ?? '';
        return {
          title: 'Status Updated',
          subtitle: previousStatus
            ? `from "${previousStatus}" to "${newStatus}"`
            : newStatus
            ? `to "${newStatus}"`
            : 'Status changed',
          reason,
          updatedBy,
          badge: 'Status Change',
          badgeColor: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      }
      break;

    case 7: // Vendor Message
      if (metadata) {
        // Support both camelCase (new) and PascalCase (legacy) keys
        const m: any = metadata as any;
        const vendorName = m.vendorName ?? m.VendorName ?? '';
        const messageType = m.messageType ?? m.MessageType ?? '';
        const priority = m.priority ?? m.Priority ?? '';
        const responseRequired = m.responseRequired ?? m.ResponseRequired ?? false;
        const contactPerson = m.contactPerson ?? m.ContactPerson ?? '';
        return {
          title: 'Vendor Message',
          subtitle: vendorName,
          messageType,
          priority,
          responseRequired,
          contactPerson,
          badge: messageType || 'Vendor',
          badgeColor: 'bg-orange-100 text-orange-800 border-orange-200'
        };
      }
      break;

    case 8: // Client Message
      if (metadata) {
        // Support both camelCase (new) and PascalCase (legacy) keys
        const m: any = metadata as any;
        const contactPerson = m.contactPerson ?? m.ContactPerson ?? '';
        const messageType = m.messageType ?? m.MessageType ?? '';
        const priority = m.priority ?? m.Priority ?? '';
        const responseRequired = m.responseRequired ?? m.ResponseRequired ?? false;
        return {
          title: 'Client Message',
          subtitle: contactPerson || 'Client communication',
          messageType,
          priority,
          responseRequired,
          contactPerson,
          badge: messageType || 'Client',
          badgeColor: 'bg-indigo-100 text-indigo-800 border-indigo-200'
        };
      }
      break;

    case 4: // File Upload
      if (metadata) {
        const fileData = metadata as FileUploadMetadata;
        return {
          title: 'File Uploaded',
          subtitle: fileData.fileName,
          fileType: fileData.fileType,
          fileSize: fileData.fileSizeBytes,
          isConfidential: fileData.isConfidential,
          badge: 'File',
          badgeColor: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      }
      break;

    case 3: // Email
      if (metadata) {
        const emailData = metadata as EmailMetadata;
        return {
          title: emailData.isOutbound ? 'Email Sent' : 'Email Received',
          subtitle: emailData.subject || (emailData.isOutbound ? 'to ' + emailData.toEmail : 'from ' + emailData.fromEmail),
          hasAttachments: emailData.hasAttachments,
          badge: emailData.isOutbound ? 'Sent' : 'Received',
          badgeColor: emailData.isOutbound 
            ? 'bg-green-100 text-green-800 border-green-200'
            : 'bg-blue-100 text-blue-800 border-blue-200'
        };
      }
      break;

    case 9: // System Message
      if (metadata) {
        // Support both camelCase (new) and PascalCase (legacy) keys
        const m: any = metadata as any;
        const messageType = m.messageType ?? m.MessageType ?? '';
        const triggeredBy = m.triggeredBy ?? m.TriggeredBy ?? '';
        const isUserVisible = m.isUserVisible ?? m.IsUserVisible ?? true;
        return {
          title: 'System Notification',
          subtitle: messageType,
          triggeredBy,
          isUserVisible,
          badge: 'System',
          badgeColor: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      }
      break;

    default:
      return {
        title: entry.entryTypeName,
        subtitle: '',
        badge: entry.entryTypeName,
        badgeColor: 'bg-gray-100 text-gray-800 border-gray-200'
      };
  }

  // Fallback for entries without metadata
  return {
    title: entry.entryTypeName || 'Unknown Entry',
    subtitle: '',
    badge: entry.entryTypeName || 'Unknown',
    badgeColor: 'bg-gray-100 text-gray-800 border-gray-200'
  };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Get the icon component for an entry type
 */
export const getEntryTypeIcon = (entryTypeId: number): LucideIcon => {
  switch (entryTypeId) {
    case 1: // Note
      return MessageSquare;
    case 2: // Call
      return Phone;
    case 3: // Email
      return Mail;
    case 4: // File Upload
      return Paperclip;
    case 5: // Include on Invoice
      return DollarSign;
    case 6: // Status Update
      return Activity;
    case 7: // Vendor Message
      return Truck;
    case 8: // Client Message
      return User;
    case 9: // System Message
      return Bot;
    default:
      return MessageSquare;
  }
};

/**
 * Get the color class for an entry type icon
 */
export const getEntryTypeIconColor = (entryTypeId: number): string => {
  switch (entryTypeId) {
    case 1: // Note
      return 'text-gray-600 dark:text-gray-400';
    case 2: // Call
      return 'text-blue-600 dark:text-blue-400';
    case 3: // Email
      return 'text-green-600 dark:text-green-400';
    case 4: // File Upload
      return 'text-purple-600 dark:text-purple-400';
    case 5: // Include on Invoice
      return 'text-green-600 dark:text-green-400';
    case 6: // Status Update
      return 'text-yellow-600 dark:text-yellow-400';
    case 7: // Vendor Message
      return 'text-orange-600 dark:text-orange-400';
    case 8: // Client Message
      return 'text-indigo-600 dark:text-indigo-400';
    case 9: // System Message
      return 'text-gray-600 dark:text-gray-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Get file type badge info from filename
 */
export const getFileTypeBadge = (fileName: string): { badge: string; color: string } => {
  const extension = fileName.toLowerCase().split('.').pop() || '';

  switch (extension) {
    case 'pdf':
      return { badge: 'PDF', color: 'bg-red-500 text-white' };
    case 'doc':
    case 'docx':
      return { badge: 'DOC', color: 'bg-blue-500 text-white' };
    case 'xls':
    case 'xlsx':
      return { badge: 'XLS', color: 'bg-green-500 text-white' };
    case 'ppt':
    case 'pptx':
      return { badge: 'PPT', color: 'bg-orange-500 text-white' };
    case 'txt':
      return { badge: 'TXT', color: 'bg-gray-500 text-white' };
    case 'csv':
      return { badge: 'CSV', color: 'bg-teal-500 text-white' };
    case 'zip':
    case 'rar':
    case '7z':
      return { badge: 'ZIP', color: 'bg-yellow-500 text-white' };
    case 'jpg':
    case 'jpeg':
      return { badge: 'JPG', color: 'bg-pink-500 text-white' };
    case 'png':
      return { badge: 'PNG', color: 'bg-purple-500 text-white' };
    case 'gif':
      return { badge: 'GIF', color: 'bg-green-500 text-white' };
    case 'svg':
      return { badge: 'SVG', color: 'bg-indigo-500 text-white' };
    case 'mp4':
    case 'avi':
    case 'mov':
      return { badge: 'VID', color: 'bg-purple-600 text-white' };
    case 'mp3':
    case 'wav':
      return { badge: 'AUD', color: 'bg-blue-600 text-white' };
    case 'js':
    case 'jsx':
      return { badge: 'JS', color: 'bg-yellow-400 text-black' };
    case 'ts':
    case 'tsx':
      return { badge: 'TS', color: 'bg-blue-600 text-white' };
    case 'css':
      return { badge: 'CSS', color: 'bg-blue-400 text-white' };
    case 'html':
      return { badge: 'HTML', color: 'bg-orange-600 text-white' };
    case 'json':
      return { badge: 'JSON', color: 'bg-gray-600 text-white' };
    default:
      return { badge: extension.toUpperCase().slice(0, 4), color: 'bg-gray-500 text-white' };
  }
};
